import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from '@core/auth/hooks/useAuth';
import { cartService, CartError } from '@features/cart/services/cartService';
import {
  transformCartItems,
  calculateItemCount,
  type CartItem,
  type CartResponse,
  type CartValidationResult,
} from '@features/cart/types/cart.types';

// Re-export CartItem for backward compatibility
export type { CartItem } from '@features/cart/types/cart.types';

interface CartContextValue {
  // State
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  /** @deprecated Use subtotal instead */
  total: number;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  stockAdjustments: string[];

  // Actions
  addItem: (item: AddItemInput, quantity?: number) => Promise<void>;
  removeItem: (variantId: number) => Promise<void>;
  updateQuantity: (variantId: number, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  validateCart: () => Promise<CartValidationResult>;
  dismissStockAdjustment: (index: number) => void;

  // Queries
  isInCart: (variantId: number) => boolean;
  getItemQuantity: (variantId: number) => number;
}

/**
 * Input for adding items to cart
 * Supports both legacy format and new format with just variantId
 */
type AddItemInput =
  | { variantId: number; maxQuantity: number; [key: string]: unknown }
  | number;

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // State
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stockAdjustments, setStockAdjustments] = useState<string[]>([]);

  // Track if initial load has happened
  const initialLoadDone = useRef(false);
  const previousAuthState = useRef<boolean | null>(null);

  // Computed values
  const itemCount = calculateItemCount(items);
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  /**
   * Fetch cart from backend
   */
  const refreshCart = useCallback(async () => {
    try {
      setIsSyncing(true);
      setError(null);
      const cartData = await cartService.getCart();
      setCart(cartData);
      setItems(transformCartItems(cartData));
    } catch (err) {
      // If cart not found or unauthorized (guest without session), just reset to empty state
      // UNAUTHORIZED happens when a guest user hasn't interacted with the cart yet
      // CART_NOT_FOUND happens after checkout when cart is converted to order
      if (err instanceof CartError && (err.code === 'CART_NOT_FOUND' || err.code === 'UNAUTHORIZED')) {
        setCart(null);
        setItems([]);
        setError(null);
      } else {
        const message = err instanceof CartError ? err.message : 'Error al cargar el carrito';
        setError(message);
      }
    } finally {
      setIsSyncing(false);
      setIsLoading(false);
    }
  }, []);

  /**
   * Initial cart load
   */
  useEffect(() => {
    if (isAuthLoading) return;

    // Load cart on initial mount
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      refreshCart();
      previousAuthState.current = isAuthenticated;
      return;
    }

    // Refresh cart when auth state changes (login/logout)
    if (previousAuthState.current !== isAuthenticated) {
      previousAuthState.current = isAuthenticated;
      refreshCart();
    }
  }, [isAuthenticated, isAuthLoading, refreshCart]);

  /**
   * Add item to cart
   */
  const addItem = useCallback(
    async (input: AddItemInput, quantity = 1) => {
      // Normalize input to get variantId
      const variantId = typeof input === 'number' ? input : input.variantId;

      try {
        setIsSyncing(true);
        setError(null);

        const result = await cartService.addItem({ variantId, quantity });

        // Update local state with server response
        setCart(result.cart);
        setItems(transformCartItems(result.cart));

        // Handle stock adjustment warning
        if (result.adjusted && result.message) {
          setStockAdjustments((prev) => [...prev, result.message!]);
        }
      } catch (err) {
        const message = err instanceof CartError ? err.message : 'Error al agregar al carrito';
        setError(message);
        throw err;
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  /**
   * Remove item from cart
   */
  const removeItem = useCallback(async (variantId: number) => {
    // Optimistic update
    const previousItems = items;
    setItems((prev) => prev.filter((item) => item.variantId !== variantId));

    try {
      setIsSyncing(true);
      setError(null);

      const result = await cartService.removeItem(variantId);
      setCart(result.cart);
      setItems(transformCartItems(result.cart));
    } catch (err) {
      // Rollback on error
      setItems(previousItems);
      const message = err instanceof CartError ? err.message : 'Error al eliminar del carrito';
      setError(message);
      throw err;
    } finally {
      setIsSyncing(false);
    }
  }, [items]);

  /**
   * Update item quantity
   */
  const updateQuantity = useCallback(
    async (variantId: number, quantity: number) => {
      if (quantity <= 0) {
        return removeItem(variantId);
      }

      // Optimistic update
      const previousItems = items;
      setItems((prev) =>
        prev.map((item) =>
          item.variantId === variantId ? { ...item, quantity } : item
        )
      );

      try {
        setIsSyncing(true);
        setError(null);

        const result = await cartService.updateItem(variantId, { quantity });
        setCart(result.cart);
        setItems(transformCartItems(result.cart));

        // Handle stock adjustment warning
        if (result.adjusted && result.message) {
          setStockAdjustments((prev) => [...prev, result.message!]);
        }
      } catch (err) {
        // Rollback on error
        setItems(previousItems);
        const message = err instanceof CartError ? err.message : 'Error al actualizar cantidad';
        setError(message);
        throw err;
      } finally {
        setIsSyncing(false);
      }
    },
    [items, removeItem]
  );

  /**
   * Clear all items from cart
   * If cart is already empty/converted, just reset local state
   */
  const clearCart = useCallback(async () => {
    // Optimistic update
    const previousItems = items;
    const previousCart = cart;
    setItems([]);
    setCart(null);

    // If no cart exists locally, just keep the cleared state
    if (!cart || items.length === 0) {
      return;
    }

    try {
      setIsSyncing(true);
      setError(null);

      const result = await cartService.clearCart();
      setCart(result.cart);
      setItems(transformCartItems(result.cart));
    } catch (err) {
      // If cart not found, keep the cleared state (cart was already converted)
      if (err instanceof CartError && err.code === 'CART_NOT_FOUND') {
        setCart(null);
        setItems([]);
        setError(null);
        return;
      }
      // Rollback on other errors
      setItems(previousItems);
      setCart(previousCart);
      const message = err instanceof CartError ? err.message : 'Error al vaciar el carrito';
      setError(message);
      throw err;
    } finally {
      setIsSyncing(false);
    }
  }, [items, cart]);

  /**
   * Validate cart before checkout
   */
  const validateCart = useCallback(async (): Promise<CartValidationResult> => {
    try {
      setIsSyncing(true);
      setError(null);
      return await cartService.validateCart();
    } catch (err) {
      const message = err instanceof CartError ? err.message : 'Error al validar el carrito';
      setError(message);
      throw err;
    } finally {
      setIsSyncing(false);
    }
  }, []);

  /**
   * Dismiss a stock adjustment warning
   */
  const dismissStockAdjustment = useCallback((index: number) => {
    setStockAdjustments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Check if a variant is in the cart
   */
  const isInCart = useCallback(
    (variantId: number) => items.some((item) => item.variantId === variantId),
    [items]
  );

  /**
   * Get quantity of a variant in the cart
   */
  const getItemQuantity = useCallback(
    (variantId: number) => {
      const item = items.find((i) => i.variantId === variantId);
      return item?.quantity ?? 0;
    },
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        total: subtotal, // Backward compatibility
        isLoading,
        isSyncing,
        error,
        stockAdjustments,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        refreshCart,
        validateCart,
        dismissStockAdjustment,
        isInCart,
        getItemQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
